import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayoutHeader } from '@client/components/_core/layout/PageLayout/PageLayoutHeader';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { Link } from '@client/components/Common/Link';
import { ClaimStatusTag } from '@client/components/claims/ClaimStatusTag';
import {
    buildInitialWhereFromFilters,
    createCommonFields,
    createFilterConfig,
    FilterBar,
} from '@client/components/filters';
import { builtInViewPayload, TableViewsBar, useTableViews } from '@client/components/table-views';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import type { ClaimStatusType } from '@shared/prisma/enums';
// Not from the filters barrel: `validateSearch` stays in the eagerly loaded route tree, and
// the barrel would pull the whole filter UI into the boot bundle.
import { FilterSchema } from '@shared/schemas/filter';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { ClipboardList } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { claimRef, offRoadDaysToneClass } from '../portal-utils';
import { PortalScopeSelect, usePortalScope } from '../usePortalScope';

export const Route = createFileRoute('/console/$orgId/fleet-portal/claims/')({
    staticData: {
        ui: {
            title: 'All claims',
            subtitle: 'Claims for your fleet clients',
            icon: ClipboardList,
        },
    },
    // `fleetClientId` (the client scope) is validated and retained by the layout route.
    validateSearch: zodValidator(
        z.object({
            claimId: z.coerce.bigint().optional().catch(undefined),
            'fleet-portal-claim-filters': z.array(FilterSchema).optional().catch(undefined),
            view: z.string().optional().catch(undefined),
        }),
    ),
    beforeLoad: ({ search, params }): void => {
        // Legacy drawer deep links (?claimId=…) now land on the full claim page.
        if (search.claimId !== undefined) {
            throw redirect({
                to: '/console/$orgId/fleet-portal/claims/details/$claimId',
                params: { orgId: params.orgId, claimId: search.claimId },
                replace: true,
            });
        }
    },
    component: FleetPortalClaimsPage,
});

type PortalClaim = RouterOutputs['fleetPortal']['listClaims']['rows'][number];
type QueryParams = Omit<RouterInputs['fleetPortal']['listClaims'], 'fleetClientId'>;
type ClaimWhere = NonNullable<QueryParams['where']>;

const commonFields = createCommonFields<ClaimWhere>();
const columnHelper = createColumnHelper<PortalClaim, QueryParams['orderBy']>();

/** Search + status filter — the status field also powers the Open/Closed built-in views. */
const filterConfig = createFilterConfig<ClaimWhere>([
    commonFields.textSearch({
        id: 'search',
        operators: ['contains'],
        defaultOperator: 'contains',
        pinned: true,
        placeholder: 'Search claims',
        customWhereBuilder: (_operator, value) => {
            const contains = { contains: value, mode: 'insensitive' } as const;
            // The vehicle column renders "make description" joined, so "Ford Ranger"
            // spans two fields — every word must match either field, not the pair as one string.
            const words = value.trim().split(/\s+/).filter(Boolean);
            // Claims without a generated/external ref render as "#<id>" — match those too.
            const idMatch = /^#?(\d{1,18})$/.exec(value.trim());
            return {
                OR: [
                    ...(idMatch?.[1] ? [{ id: BigInt(idMatch[1]) }] : []),
                    { generatedId: contains },
                    { externalId: contains },
                    { causeOfLoss: contains },
                    { FleetVehicle: { registration: contains } },
                    ...(words.length
                        ? [
                              {
                                  FleetVehicle: {
                                      AND: words.map((word) => ({
                                          OR: [
                                              { make: { contains: word, mode: 'insensitive' as const } },
                                              { description: { contains: word, mode: 'insensitive' as const } },
                                          ],
                                      })),
                                  },
                              },
                          ]
                        : []),
                ],
            };
        },
    }),
    commonFields.multiselect<ClaimStatusType, 'in'>({
        id: 'status',
        label: 'Status',
        operators: ['in'],
        defaultOperator: 'in',
        options: [
            { label: 'Open', value: 'OPEN' },
            { label: 'Closed', value: 'CLOSED' },
        ],
        customWhereBuilder: (_operator, value) => ({ Status: { type: { in: value } } }),
    }),
    commonFields.date({
        id: 'lodged',
        label: 'Lodged',
        dbField: 'createdAt',
    }),
    commonFields.date({
        id: 'closed',
        label: 'Closed',
        dbField: 'dtClosed',
    }),
    // The coordinator's Pending authorisations tile lands here (CUR-1100); a
    // fleet manager can use it too. Only the request status is filterable.
    commonFields.boolean({
        id: 'awaitingAuthorisation',
        label: 'Awaiting authorisation',
        operators: ['equals'],
        defaultOperator: 'equals',
        customWhereBuilder: (_operator, value) =>
            value === undefined
                ? null
                : { ClaimAuthorisationRequests: { [value ? 'some' : 'none']: { status: 'PENDING' } } },
    }),
]);

const builtInViews = [
    {
        id: '__open__',
        name: 'Open',
        description: 'Open claims',
        payload: builtInViewPayload([{ id: 'view-open', filterId: 'status', operator: 'in', value: ['OPEN'] }]),
    },
    {
        id: '__closed__',
        name: 'Closed',
        description: 'Closed claims',
        payload: builtInViewPayload([{ id: 'view-closed', filterId: 'status', operator: 'in', value: ['CLOSED'] }]),
    },
];

function FleetPortalClaimsPage() {
    return (
        <PageController route={Route}>
            <ClaimsContent />
        </PageController>
    );
}

function ClaimsContent() {
    const scope = usePortalScope();

    // Column visibility and saved views initialise once. Resolve the persona before
    // mounting their state so a cold coordinator visit includes the Client column.
    if (scope.persona === undefined) {
        return (
            <div>
                <PageLayoutHeader rightAddon={<PortalScopeSelect />} />
                {scope.error ? (
                    <InlineQueryError error={scope.error} onRetry={scope.refetch} />
                ) : (
                    <Loading description="Loading claims" />
                )}
            </div>
        );
    }

    return <ClaimsTable key={scope.persona} />;
}

function ClaimsTable() {
    const orgId = useOrgId();
    const { trpc } = useTrpc();
    const { formatDate, formatCurrency } = useFormatter();
    const { 'fleet-portal-claim-filters': searchFilters, view: searchView } = Route.useSearch();
    const navigate = useNavigate();
    const scope = usePortalScope();
    const [viewsReady, setViewsReady] = useState(false);
    const previousFleetClientId = useRef(scope.fleetClientId);

    const [queryParams, setQueryParams] = usePathState<QueryParams>('claim-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: [{ createdAt: { sort: 'desc', nulls: 'last' } }, { id: 'desc' }],
        where: buildInitialWhereFromFilters(filterConfig, searchFilters),
    });

    // Changing the client scope narrows the result set — jump back to the first page so a
    // portfolio user isn't left on a now-out-of-range offset.
    useEffect(() => {
        if (previousFleetClientId.current === scope.fleetClientId) {
            return;
        }
        previousFleetClientId.current = scope.fleetClientId;
        setQueryParams((prev) => ({ ...prev, offset: 0 }));
    }, [scope.fleetClientId, setQueryParams]);

    const listClaimsQuery = trpc.fleetPortal.listClaims.useQuery(
        { ...queryParams, fleetClientId: scope.fleetClientId },
        { enabled: viewsReady && !scope.isLoading && !scope.error },
    );

    const tableProps = usePrismaTable({
        data: listClaimsQuery.data?.rows ?? [],
        total: listClaimsQuery.data?.total ?? 0,
        loading: !viewsReady || listClaimsQuery.isLoading,
        rowKey: (row) => row.id.toString(),
        queryParams,
        onParamsChange: setQueryParams,
        emptyState: { noRecordsTitle: 'No claims yet' },
        columns: [
            columnHelper.accessor('generatedId', {
                id: 'claim',
                header: 'Claim',
                className: 'whitespace-nowrap',
                cell: ({ row }) => (
                    <Link
                        to="/console/$orgId/fleet-portal/claims/details/$claimId"
                        params={{ orgId, claimId: row.id }}
                        className="font-bold"
                    >
                        {claimRef(row)}
                    </Link>
                ),
                hideable: false,
            }),
            // Rows are attributable in the coordinator's All clients view (CUR-1100);
            // a fleet manager's list is unchanged.
            ...(scope.persona === 'FMC_COORDINATOR'
                ? [
                      columnHelper.accessor('clientName', {
                          id: 'client',
                          header: 'Client',
                          cell: ({ row }) => row.clientName ?? '—',
                      }),
                  ]
                : []),
            columnHelper.accessor('registration', {
                id: 'registration',
                header: 'Rego',
                cell: ({ row }) => row.registration ?? '—',
            }),
            columnHelper.accessor('vehicle', {
                id: 'vehicle',
                header: 'Vehicle',
                cell: ({ row }) => row.vehicle ?? '—',
            }),
            columnHelper.accessor('driver', {
                id: 'driver',
                header: 'Driver',
                cell: ({ row }) => row.driver ?? '—',
            }),
            columnHelper.accessor('causeOfLoss', {
                id: 'causeOfLoss',
                header: 'Cause of loss',
                cell: ({ row }) => row.causeOfLoss ?? '—',
            }),
            columnHelper.accessor('statusName', {
                id: 'status',
                header: 'Status',
                className: 'whitespace-nowrap',
                cell: ({ row }) => (
                    <ClaimStatusTag data={{ name: row.statusName, displayColor: row.statusDisplayColor }} />
                ),
            }),
            columnHelper.accessor('createdAt', {
                id: 'lodged',
                header: 'Lodged',
                sortable: { field: 'createdAt', nulls: 'last' },
                cell: ({ row }) => formatDate(row.createdAt),
            }),
            columnHelper.accessor('dtClosed', {
                id: 'closed',
                header: 'Closed',
                cell: ({ row }) => (row.dtClosed ? formatDate(row.dtClosed) : '—'),
            }),
            columnHelper.accessor('repairStage', {
                id: 'repairStage',
                header: 'Repair stage',
                cell: ({ row }) => row.repairStage ?? '—',
            }),
            // Days off road only while genuinely undriveable (a total loss counts
            // the same); distinguish a positive driveable answer from unknown.
            columnHelper.accessor('offRoad', {
                id: 'offRoad',
                header: 'Off road',
                className: 'whitespace-nowrap',
                cell: ({ row }) =>
                    row.offRoad === null ? (
                        <span className="text-muted-foreground">
                            {row.isDrivable === true ? 'Driveable' : 'Unknown'}
                        </span>
                    ) : (
                        <span className={`font-semibold ${offRoadDaysToneClass(row.offRoad.days)}`}>
                            {row.offRoad.days === null
                                ? 'Unknown duration'
                                : `${row.offRoad.days} ${row.offRoad.days === 1 ? 'day' : 'days'}`}
                            {row.offRoad.isTotalLoss ? ' · total loss' : ''}
                        </span>
                    ),
            }),
            columnHelper.accessor('excessPayable', {
                id: 'excess',
                header: 'Excess',
                align: 'right',
                cell: ({ row }) => (row.excessPayable === null ? '—' : formatCurrency(Number(row.excessPayable))),
            }),
            columnHelper.display({
                id: 'actions',
                // Blank on screen; the table names it "Actions" for assistive tech (CUR-1341).
                header: '',
                align: 'right',
                hideable: false,
                cell: ({ row }) => (
                    <Button.View.Link
                        to="/console/$orgId/fleet-portal/claims/details/$claimId"
                        params={{ orgId, claimId: row.id }}
                    />
                ),
            }),
        ],
    });

    const views = useTableViews({
        entityType: 'CLAIM',
        config: filterConfig,
        configReady: true,
        urlFilterKey: 'fleet-portal-claim-filters',
        builtInViews,
        queryParams,
        setQueryParams,
        table: tableProps,
        columnPersistKey: 'fleet-portal-claims-columns',
        defaultPageSize: DEFAULT_PAGE_SIZE,
        initialFilters: searchFilters,
        initialActiveViewId: searchView,
        onResolved: () => setViewsReady(true),
    });

    return (
        <div>
            <PageLayoutHeader rightAddon={<PortalScopeSelect />} />

            {scope.error ? (
                <InlineQueryError error={scope.error} onRetry={scope.refetch} />
            ) : (
                <>
                    <TableViewsBar controller={views.controller} />
                    <TableToolbar
                        variant="enhanced"
                        filters={
                            <FilterBar
                                config={filterConfig}
                                filters={views.filterState.filters}
                                whereClause={views.filterState.whereClause}
                                onAddFilter={views.filterState.addFilter}
                                onUpdateFilter={views.filterState.updateFilter}
                                onRemoveFilter={views.filterState.removeFilter}
                                onClearFilters={views.filterState.clearFilters}
                            />
                        }
                    />

                    {listClaimsQuery.error ? (
                        <InlineQueryError
                            error={listClaimsQuery.error}
                            onRetry={() => void listClaimsQuery.refetch()}
                        />
                    ) : (
                        <PrismaTable
                            {...tableProps}
                            onRow={({ id }) => ({
                                className: 'cursor-pointer',
                                onClick(e) {
                                    // The claim link and the view button handle their own
                                    // navigation (and cmd+click) — don't double-navigate.
                                    if ((e.target as HTMLElement).closest('a, button')) {
                                        return;
                                    }
                                    navigate(
                                        {
                                            to: '/console/$orgId/fleet-portal/claims/details/$claimId',
                                            params: { orgId, claimId: id },
                                        },
                                        e,
                                    );
                                },
                            })}
                        />
                    )}
                </>
            )}
        </div>
    );
}
